import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { TriggeredMessagesRequest } from '../types';
import { PaginationUpdateEvent } from 'ui/paginationFooter';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { getKeyByValue } from '@iterable/utils';

const isValidSortByRequest = (col: string): col is TriggeredMessagesRequest['orderBy'] =>
  Object.values(sortByRequest).includes(col);

// BE Request properties are different from Results
const sortByRequest: Record<string, TriggeredMessagesRequest['orderBy']> = {
  userIdentifier: 'email',
  campaignId: 'campaignId',
  sendTimeMillis: 'createdAt',
};

export const useTablePaginationAndSorting = ({
  messageRequestParams,
  setMessageRequestParams,
}: {
  messageRequestParams: TriggeredMessagesRequest;
  setMessageRequestParams: Dispatch<SetStateAction<TriggeredMessagesRequest>>;
}) => {
  const initialSortedBy = getKeyByValue(
    sortByRequest,
    messageRequestParams.orderBy,
    'sendTimeMillis',
  );
  const [sortDirection, setSortDirection] = useState<SORT_DIRECTIONS | string>(
    SORT_DIRECTIONS.DESCENDING,
  );
  const [sortedBy, setSortedBy] = useState(initialSortedBy);

  const handlePaginationUpdate = ({ page, pageSize }: PaginationUpdateEvent): void =>
    setMessageRequestParams((prevParams) => ({ ...prevParams, page, pageSize }));

  useEffect(() => {
    if (!isValidSortByRequest(sortByRequest[sortedBy])) return;
    setMessageRequestParams((prevParams) => ({ ...prevParams, orderBy: sortByRequest[sortedBy] }));
  }, [sortedBy, setMessageRequestParams]);

  useEffect(() => {
    setMessageRequestParams((prevParams) => ({
      ...prevParams,
      sortDesc: sortDirection === SORT_DIRECTIONS.DESCENDING,
    }));
  }, [sortDirection, setMessageRequestParams]);

  return {
    sortDirection,
    setSortDirection,
    sortedBy,
    setSortedBy,
    handlePaginationUpdate,
  };
};
